import {
  User,
  Organization,
  Role,
  Permission,
  AuditLog,
  DashboardStats,
  UserAnalytics,
  APIResponse,
  PaginatedResponse,
  ListUsersRequest,
  CreateUserRequest,
  UpdateUserRequest,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.dev.auth.isaacmccourt.dev/v1';

// Debug logging to see what URL is being used
console.log('API.ts - VITE_API_BASE_URL environment variable:', import.meta.env.VITE_API_BASE_URL);
console.log('API.ts - Final API_BASE_URL being used:', API_BASE_URL);

class AdminAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
    console.log('AdminAPI initialized with baseUrl:', this.baseUrl);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem('admin_access_token');
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Dashboard Analytics
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.request<APIResponse<DashboardStats>>('/admin/analytics/dashboard');
    return response.data!;
  }

  async getUserAnalytics(dateRange?: { start: string; end: string }): Promise<UserAnalytics> {
    const params = dateRange ? `?start=${dateRange.start}&end=${dateRange.end}` : '';
    const response = await this.request<APIResponse<UserAnalytics>>(`/admin/analytics/users${params}`);
    return response.data!;
  }

  async getAnalytics(dateRange?: { start: string; end: string }): Promise<UserAnalytics> {
    return this.getUserAnalytics(dateRange);
  }

  // User Management
  async getUsers(params: ListUsersRequest = {}): Promise<PaginatedResponse<User>> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    return this.request<PaginatedResponse<User>>(
      `/admin/users?${searchParams.toString()}`
    );
  }

  async getUser(userId: string): Promise<User> {
    const response = await this.request<APIResponse<{ user: User }>>(`/admin/users/${userId}`);
    return response.data!.user;
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await this.request<APIResponse<{ user: User }>>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return response.data!.user;
  }

  async updateUser(userId: string, userData: UpdateUserRequest): Promise<User> {
    const response = await this.request<APIResponse<{ user: User }>>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return response.data!.user;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.request(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async disableUser(userId: string): Promise<void> {
    await this.request(`/admin/users/${userId}/disable`, {
      method: 'POST',
    });
  }

  async enableUser(userId: string): Promise<void> {
    await this.request(`/admin/users/${userId}/enable`, {
      method: 'POST',
    });
  }

  async resendVerificationEmail(userId: string): Promise<void> {
    await this.request(`/admin/users/${userId}/resend-verification`, {
      method: 'POST',
    });
  }

  async resetUserPassword(userId: string): Promise<{ temporaryPassword: string }> {
    const response = await this.request<APIResponse<{ temporaryPassword: string }>>(
      `/admin/users/${userId}/reset-password`,
      {
        method: 'POST',
      }
    );
    return response.data!;
  }

  // Organization Management
  async getOrganizations(params?: { 
    page?: number; 
    limit?: number; 
    search?: string;
    status?: string;
    sort?: string;
    order?: string;
  }): Promise<PaginatedResponse<Organization>> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    } else {
      searchParams.append('page', '1');
      searchParams.append('limit', '20');
    }

    return this.request<PaginatedResponse<Organization>>(
      `/admin/organizations?${searchParams.toString()}`
    );
  }

  async createOrganization(orgData: Partial<Organization>): Promise<Organization> {
    const response = await this.request<APIResponse<{ organization: Organization }>>(
      '/admin/organizations',
      {
        method: 'POST',
        body: JSON.stringify(orgData),
      }
    );
    return response.data!.organization;
  }

  async getOrganization(orgId: string): Promise<Organization> {
    const response = await this.request<APIResponse<{ organization: Organization }>>(
      `/admin/organizations/${orgId}`
    );
    return response.data!.organization;
  }

  async updateOrganization(orgId: string, data: Partial<Organization>): Promise<Organization> {
    const response = await this.request<APIResponse<{ organization: Organization }>>(
      `/admin/organizations/${orgId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data!.organization;
  }

  async deleteOrganization(orgId: string): Promise<void> {
    await this.request(`/admin/organizations/${orgId}`, {
      method: 'DELETE',
    });
  }

  async getOrganizationMembers(orgId: string): Promise<User[]> {
    const response = await this.request<APIResponse<{ members: User[] }>>(
      `/orgs/${orgId}/members`
    );
    return response.data!.members;
  }

  async addOrganizationMember(orgId: string, userId: string): Promise<void> {
    await this.request(`/orgs/${orgId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async removeOrganizationMember(orgId: string, userId: string): Promise<void> {
    await this.request(`/orgs/${orgId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async sendOrganizationInvite(orgId: string, email: string, role: string): Promise<void> {
    await this.request(`/orgs/${orgId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async getOrganizationUsage(orgId: string): Promise<{ 
    apiCalls: number; 
    storage: number; 
    users: number; 
  }> {
    const response = await this.request<APIResponse<{ 
      apiCalls: number; 
      storage: number; 
      users: number; 
    }>>(`/admin/organizations/${orgId}/usage`);
    return response.data!;
  }

  async updateMemberRole(orgId: string, userId: string, role: string): Promise<void> {
    await this.request(`/orgs/${orgId}/members/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // ── Branding ──
  /** Get a presigned URL, upload the file to S3, and return the public URL. */
  async uploadOrgLogo(orgId: string, file: File): Promise<string> {
    const presign = await this.request<APIResponse<{ uploadUrl: string; publicUrl: string }>>(
      `/admin/organizations/${orgId}/branding/logo`,
      { method: 'POST', body: JSON.stringify({ contentType: file.type || 'image/png' }) }
    );
    const { uploadUrl, publicUrl } = presign.data!;
    const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'image/png' }, body: file });
    if (!put.ok) throw new Error(`Logo upload failed: HTTP ${put.status}`);
    return publicUrl;
  }

  // ── Billing ──
  async getOrganizationBilling(orgId: string): Promise<{ configured: boolean; subscription: any; invoices: any[] }> {
    const r = await this.request<APIResponse<{ configured: boolean; subscription: any; invoices: any[] }>>(
      `/admin/organizations/${orgId}/billing`
    );
    return r.data!;
  }

  // ── DB backups ──
  async getBackups(): Promise<any[]> {
    const r = await this.request<APIResponse<{ backups: any[] }>>('/admin/backups');
    return r.data!.backups;
  }
  async createBackup(): Promise<any> {
    const r = await this.request<APIResponse<{ backup: any }>>('/admin/backups', { method: 'POST' });
    return r.data!.backup;
  }

  // ── Email / SMTP ──
  async getEmailSettings(): Promise<{ configured: boolean; host: string; port: number; user: string; from: string }> {
    const r = await this.request<APIResponse<any>>('/admin/settings/email');
    return r.data!;
  }
  async updateEmailSettings(cfg: { host: string; port: number; user?: string; pass?: string; from?: string }): Promise<void> {
    await this.request('/admin/settings/email', { method: 'PUT', body: JSON.stringify(cfg) });
  }
  async testEmail(to: string): Promise<{ sent: boolean }> {
    const r = await this.request<APIResponse<{ sent: boolean }>>('/admin/settings/email/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    });
    return r.data!;
  }

  // ── Email template editor (per-org overrides + localized defaults) ──
  async getEmailTemplates(orgId = 'global'): Promise<{ keys: string[]; locales: string[]; defaults: any; overrides: any }> {
    const r = await this.request<APIResponse<any>>(`/admin/settings/email-templates?orgId=${orgId}`);
    return r.data!;
  }
  async saveEmailTemplate(key: string, tpl: { subject: string; html: string; text?: string }, orgId = 'global'): Promise<void> {
    await this.request(`/admin/settings/email-templates?orgId=${orgId}`, { method: 'PUT', body: JSON.stringify({ key, ...tpl }) });
  }
  async resetEmailTemplate(key: string, orgId = 'global'): Promise<void> {
    await this.request(`/admin/settings/email-templates?orgId=${orgId}`, { method: 'PUT', body: JSON.stringify({ key, reset: true }) });
  }

  // ── SSO config ──
  async getOrgSso(orgId: string): Promise<{ oidc: any; saml: any }> {
    const r = await this.request<APIResponse<{ oidc: any; saml: any }>>(`/admin/organizations/${orgId}/sso`);
    return r.data!;
  }
  async updateOrgSso(orgId: string, cfg: { oidc?: any; saml?: any }): Promise<void> {
    await this.request(`/admin/organizations/${orgId}/sso`, { method: 'PUT', body: JSON.stringify(cfg) });
  }

  // Role Management
  async getRoles(params?: { page?: number; limit?: number; search?: string; orgId?: string }): Promise<PaginatedResponse<Role>> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }

    return this.request<PaginatedResponse<Role>>(`/authz/roles?${searchParams.toString()}`);
  }

  async getRole(roleId: string): Promise<Role> {
    const response = await this.request<APIResponse<{ role: Role }>>(`/authz/roles/${roleId}`);
    return response.data!.role;
  }

  async createRole(roleData: {
    name: string;
    description?: string;
    permissions: string[];
    orgId?: string;
  }): Promise<Role> {
    const response = await this.request<APIResponse<{ role: Role }>>('/authz/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
    return response.data!.role;
  }

  async updateRole(roleId: string, roleData: {
    name?: string;
    description?: string;
    permissions?: string[];
  }): Promise<Role> {
    const response = await this.request<APIResponse<{ role: Role }>>(`/authz/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    });
    return response.data!.role;
  }

  async deleteRole(roleId: string): Promise<void> {
    await this.request(`/authz/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  async assignRole(userId: string, roleId: string, orgId?: string): Promise<void> {
    await this.request('/authz/users/assign-role', {
      method: 'POST',
      body: JSON.stringify({ userId, roleId, orgId }),
    });
  }

  async removeRole(userId: string, roleId: string): Promise<void> {
    await this.request(`/authz/users/${userId}/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  async getPermissions(): Promise<Permission[]> {
    const response = await this.request<APIResponse<{ permissions: Permission[] }>>('/authz/permissions');
    return response.data!.permissions;
  }

  // Audit Logs
  async getAuditLogs(params: {
    page?: number;
    limit?: number;
    userId?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<PaginatedResponse<AuditLog>> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await this.request<APIResponse<PaginatedResponse<AuditLog>>>(
      `/admin/audit-logs?${searchParams.toString()}`
    );
    return response.data!;
  }

  async exportAuditLogs(format: 'csv' | 'json', filters?: Record<string, any>): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await fetch(`${this.baseUrl}/admin/audit-logs/export?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('admin_access_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to export audit logs');
    }

    return response.blob();
  }

  // Session Management
  async getUserSessions(userId: string): Promise<any[]> {
    const response = await this.request<APIResponse<{ sessions: any[] }>>(
      `/admin/users/${userId}/sessions`
    );
    return response.data!.sessions;
  }

  async revokeUserSession(userId: string, sessionId: string): Promise<void> {
    await this.request(`/admin/users/${userId}/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async revokeAllUserSessions(userId: string): Promise<void> {
    await this.request(`/admin/users/${userId}/sessions`, {
      method: 'DELETE',
    });
  }

  // System Settings
  async getSystemSettings(): Promise<any> {
    const response = await this.request<APIResponse<any>>('/admin/settings');
    return response.data;
  }

  async updateSystemSettings(settings: any): Promise<any> {
    const response = await this.request<APIResponse<any>>('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    return response.data;
  }

  // ── System health & security alerts ──
  async getHealth(): Promise<{ status: string; components: { name: string; status: string; detail?: string }[] }> {
    const r = await this.request<APIResponse<any>>('/admin/health');
    return r.data!;
  }
  async getSecurityAlerts(): Promise<{ alerts: { severity: string; title: string; detail: string }[] }> {
    const r = await this.request<APIResponse<any>>('/admin/security-alerts');
    return r.data!;
  }

  // ── Webhooks ──
  async getWebhooks(): Promise<any[]> {
    const r = await this.request<APIResponse<{ webhooks: any[] }>>('/admin/webhooks');
    return r.data!.webhooks;
  }
  async createWebhook(input: { url: string; events: string[]; description?: string }): Promise<any> {
    const r = await this.request<APIResponse<{ webhook: any }>>('/admin/webhooks', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return r.data!.webhook; // includes the signing secret (shown once)
  }
  async updateWebhook(id: string, patch: { url?: string; events?: string[]; active?: boolean; description?: string }): Promise<any> {
    const r = await this.request<APIResponse<{ webhook: any }>>(`/admin/webhooks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    return r.data!.webhook;
  }
  async deleteWebhook(id: string): Promise<void> {
    await this.request(`/admin/webhooks/${id}`, { method: 'DELETE' });
  }
  async testWebhook(id: string): Promise<{ delivered: boolean; status?: number; error?: string }> {
    const r = await this.request<APIResponse<any>>(`/admin/webhooks/${id}/test`, { method: 'POST' });
    return r.data!;
  }

  // ── Developer console (apps + API keys). Super-admins can mint admin-tier keys. ──
  async getApiKeyScopes(): Promise<{ scopes: string[]; adminScopes: string[] }> {
    const r = await this.request<APIResponse<any>>('/console/scopes');
    return { scopes: r.data!.scopes, adminScopes: r.data!.adminScopes ?? [] };
  }
  async getApps(): Promise<any[]> {
    const r = await this.request<APIResponse<any>>('/console/apps');
    return r.data!.applications;
  }
  async getApp(appId: string): Promise<{ application: any; keys: any[] }> {
    const r = await this.request<APIResponse<any>>(`/console/apps/${appId}`);
    return r.data!;
  }
  async createApp(name: string): Promise<any> {
    const r = await this.request<APIResponse<any>>('/console/apps', { method: 'POST', body: JSON.stringify({ name }) });
    return r.data!.application;
  }
  async deleteApp(appId: string): Promise<void> {
    await this.request(`/console/apps/${appId}`, { method: 'DELETE' });
  }
  async issueApiKey(appId: string, scopes: string[], name?: string): Promise<{ key: any; secret: string }> {
    const r = await this.request<APIResponse<any>>(`/console/apps/${appId}/keys`, { method: 'POST', body: JSON.stringify({ scopes, name }) });
    return r.data!;
  }
  async rotateApiKey(appId: string, keyId: string): Promise<{ key: any; secret: string }> {
    const r = await this.request<APIResponse<any>>(`/console/apps/${appId}/keys/${keyId}/rotate`, { method: 'POST' });
    return r.data!;
  }
  async revokeApiKey(appId: string, keyId: string): Promise<void> {
    await this.request(`/console/apps/${appId}/keys/${keyId}`, { method: 'DELETE' });
  }
}

export const adminAPI = new AdminAPI();
